import { useState, useRef } from 'react';
import ArtZipButton from './ArtZipButton';
import BatchCompletion from './BatchCompletion';
import SourceSheetLink from './SourceSheetLink';

const BACKBOOK_SHEET_URL =
  'https://docs.google.com/spreadsheets/d/1W0S0O2ImjUY5BMLGbNGkeHIH2L9_1i5KYUyaKGCEYyA/edit?gid=833863373#gid=833863373';

function localBatchDate() {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Backbook "modify & ship": Capital One's sheet supplies ship-to addresses for
 * existing card/stand order pairs that never got greenlit. Applying updates
 * the addresses, sets production/full_kit/authorized, and batches cards vs
 * stands. Only the stands batch has downloads — cards are pre-produced, and
 * the stands CSV's CARD columns tell production what to pull off the shelf.
 */
export default function BackbookModal({ onClose, onComplete, secretKey, onNeedAuth, onAuthFailed }) {
  const [step, setStep] = useState('select'); // select → preview → applied
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [confirmSubset, setConfirmSubset] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef();
  const batchDate = localBatchDate();

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f) setFile(f);
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/backbook/preview', { method: 'POST', body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to parse file');
      setPreview(data);
      setStep('preview');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (mode) => {
    if (!secretKey) {
      onNeedAuth();
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('secret', secretKey);
      formData.append('mode', mode);
      formData.append('batchDate', batchDate);
      const res = await fetch('/api/backbook/apply', { method: 'POST', body: formData });
      const data = await res.json();
      if (res.status === 403) {
        onAuthFailed();
        return;
      }
      if (res.status === 409) {
        setPreview({ pairs: data.pairs, totals: data.totals, ok: false });
        setConfirmAll(false);
        setConfirmSubset(false);
        setError(data.error);
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Apply failed');
      setResult(data);
      setStep('applied');
      if (onComplete) onComplete();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const validPairs = preview?.totals?.valid || 0;
  const invalidPairs = preview?.totals?.withIssues || 0;
  const standsBatch = result?.batches?.find((b) => b.group === 'stands');

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Backbook — Modify &amp; Ship</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {step === 'select' && "Upload Capital One's backbook shipping sheet (CSV)"}
              {step === 'preview' && 'Review dealer pairs before updating orders'}
              {step === 'applied' && 'Orders updated and batched'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {step === 'select' && (
            <div className="flex flex-col items-center py-6">
              <div className="w-full max-w-md">
                <SourceSheetLink href={BACKBOOK_SHEET_URL} tone="sky" label="to see the latest batches." />
              </div>
              <div
                onClick={() => fileRef.current?.click()}
                className="w-full max-w-md border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-sky-400 hover:bg-sky-50/30 transition-colors"
              >
                <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
                {file ? (
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                ) : (
                  <>
                    <p className="text-sm font-medium text-gray-600">Click to select the backbook CSV</p>
                    <p className="text-xs text-gray-400 mt-1">Ship-to addresses for existing card + stand order pairs</p>
                  </>
                )}
              </div>
              <input ref={fileRef} type="file" accept=".csv" onChange={handleFileSelect} className="hidden" />
            </div>
          )}

          {step === 'preview' && preview && (
            <>
              {/* Planned batches */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                <div className="rounded-lg px-4 py-3 border bg-sky-50 border-sky-200">
                  <p className="text-xs text-sky-700 font-medium">Cards (pre-produced — no downloads)</p>
                  <p className="text-sm font-bold font-mono text-gray-900 mt-1">BB{batchDate}_cards</p>
                  <p className="text-xs text-gray-500 mt-0.5">{validPairs} order{validPairs !== 1 ? 's' : ''}</p>
                </div>
                <div className="rounded-lg px-4 py-3 border bg-sky-50 border-sky-200">
                  <p className="text-xs text-sky-700 font-medium">Stands (CSV + art zip after apply)</p>
                  <p className="text-sm font-bold font-mono text-gray-900 mt-1">BB{batchDate}_stands</p>
                  <p className="text-xs text-gray-500 mt-0.5">{validPairs} order{validPairs !== 1 ? 's' : ''}</p>
                </div>
              </div>

              {invalidPairs > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 mb-4 text-sm text-red-800">
                  <span className="font-semibold">Review required:</span> {invalidPairs} dealer pair
                  {invalidPairs !== 1 ? 's have' : ' has'} issues. Fix the sheet, or apply the valid subset below.
                </div>
              )}

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-[38vh]">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="border-b border-gray-200">
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Dealer</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Company</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Cards Order</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Qty</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Stands Order</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Ship To</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-400 uppercase">Check</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {preview.pairs.map((p) => (
                        <tr key={p.dealerId} className={p.valid ? '' : 'bg-red-50'}>
                          <td className="px-4 py-2 font-mono text-xs font-semibold text-gray-900">{p.dealerId}</td>
                          <td className="px-4 py-2 text-xs text-gray-600">{p.company || '—'}</td>
                          <td className="px-4 py-2 font-mono text-xs text-gray-600">{p.cardsOrder?.uid || '—'}</td>
                          <td className="px-4 py-2 text-xs tabular-nums">
                            {p.cardsOrder ? (
                              <span className={p.cardsOrder.qty === 20 || p.cardsOrder.qty === 40 ? 'text-gray-600' : 'text-red-600 font-semibold'}>
                                {p.cardsOrder.qty}
                              </span>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-2 font-mono text-xs text-gray-600">{p.standsOrder?.uid || '—'}</td>
                          <td className="px-4 py-2 text-xs text-gray-500">{p.shipTo || '—'}</td>
                          <td className="px-4 py-2 text-xs">
                            {p.valid ? (
                              <span className="text-emerald-600 font-medium">Ready</span>
                            ) : (
                              p.issues.map((issue, i) => (
                                <span key={i} className="inline-block mr-1 mb-0.5 text-[10px] font-medium text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                                  {issue}
                                </span>
                              ))
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Applying updates ship-to fields + order_data_date, sets status → production, type → full_kit,
                send_authorized → TRUE, and stamps the batch labels. Roles come from product composition
                (cards-only vs one stand of each type), cross-checked against the lower-number-is-cards rule.
              </p>
            </>
          )}

          {step === 'applied' && result && (
            <div className="flex flex-col items-center justify-center py-6">
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                {result.applied} Pair{result.applied !== 1 ? 's' : ''} Updated
              </h3>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 mb-5 text-xs text-emerald-800 space-y-1">
                <p>✓ {result.updated} orders updated: addresses set, status → <span className="font-mono font-semibold">production</span>, type → <span className="font-mono font-semibold">full_kit</span></p>
                <p>✓ send_authorized → <span className="font-mono font-semibold">TRUE</span>, order_data_date from sheet</p>
                <p>✓ Batches: <span className="font-mono font-semibold">{result.batches.map((b) => b.label).join(', ')}</span></p>
              </div>

              {standsBatch && (
                <div className="w-full max-w-md border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-mono font-semibold text-gray-900">{standsBatch.label}</p>
                    <p className="text-xs text-gray-400">{standsBatch.orders} stands order{standsBatch.orders !== 1 ? 's' : ''} · print + kit</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <a
                      href={`/api/greenlight/export/${encodeURIComponent(standsBatch.label)}`}
                      download
                      className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                      </svg>
                      CSV
                    </a>
                    <ArtZipButton
                      label={standsBatch.label}
                      className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                    >
                      Art ZIP
                    </ArtZipButton>
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-400 mb-4 max-w-md text-center">
                The cards batch has no downloads — those cards are already produced. The stands CSV's CARD
                columns tell production which cards to pull for each kit.
              </p>

              {result.skipped.length > 0 && (
                <p className="text-xs text-amber-700 mb-4 max-w-md text-center">
                  Skipped (untouched): {result.skipped.map((s) => s.dealerId).join(', ')}
                </p>
              )}

              <BatchCompletion
                batches={result.batches.map((b) => ({ label: b.label, orders: b.orders }))}
                batchDate={batchDate}
                secretKey={secretKey}
                onNeedAuth={onNeedAuth}
                onAuthFailed={onAuthFailed}
                onSent={onComplete}
              />
            </div>
          )}
        </div>

        {/* Apply confirmation */}
        {step === 'preview' && confirmAll && (
          <div className="mx-6 mt-2 bg-emerald-50 border border-emerald-300 rounded-xl px-5 py-4 min-h-0 overflow-y-auto max-h-[35vh]">
            <p className="text-sm font-semibold text-emerald-900 mb-1.5">
              Update {validPairs * 2} orders ({validPairs} pair{validPairs !== 1 ? 's' : ''})?
            </p>
            <p className="text-xs text-emerald-800">
              Sets ship-to addresses, status → <span className="font-mono font-semibold">production</span>,
              type → <span className="font-mono font-semibold">full_kit</span>, send_authorized →{' '}
              <span className="font-mono font-semibold">TRUE</span>, and creates{' '}
              <span className="font-mono">BB{batchDate}_cards</span> + <span className="font-mono">BB{batchDate}_stands</span>.
            </p>
          </div>
        )}
        {step === 'preview' && confirmSubset && (
          <div className="mx-6 mt-2 bg-amber-50 border border-amber-300 rounded-xl px-5 py-4 min-h-0 overflow-y-auto max-h-[35vh]">
            <p className="text-sm font-semibold text-amber-900 mb-1.5">
              Apply {validPairs} of {preview.totals.pairs} pairs?
            </p>
            <p className="text-xs text-amber-800">
              {invalidPairs} pair{invalidPairs !== 1 ? 's' : ''} will be <span className="font-semibold">skipped</span> and
              left untouched: <span className="font-mono">{preview.pairs.filter((p) => !p.valid).map((p) => p.dealerId).join(', ')}</span>
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 flex-shrink-0">
          {step === 'select' && (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors">
                Cancel
              </button>
              <button
                onClick={handlePreview}
                disabled={!file || loading}
                className="px-5 py-2 bg-sky-700 text-white text-sm font-medium rounded-lg hover:bg-sky-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Checking…' : 'Check File'}
              </button>
            </>
          )}
          {step === 'preview' && (confirmAll || confirmSubset) && (
            <>
              <button
                onClick={() => { setConfirmAll(false); setConfirmSubset(false); }}
                disabled={loading}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-40 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleApply(confirmAll ? 'all' : 'subset')}
                disabled={loading}
                className={`px-5 py-2 text-white text-sm font-medium rounded-lg disabled:opacity-40 transition-colors ${
                  confirmAll ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'
                }`}
              >
                {loading
                  ? 'Applying…'
                  : confirmAll
                    ? `Yes — update ${validPairs * 2} orders`
                    : `Yes — apply ${validPairs}, skip ${invalidPairs}`}
              </button>
            </>
          )}
          {step === 'preview' && !confirmAll && !confirmSubset && (
            <>
              <button
                onClick={() => { setStep('select'); setPreview(null); setError(null); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Back
              </button>
              {invalidPairs > 0 && validPairs > 0 && (
                <button
                  onClick={() => setConfirmSubset(true)}
                  disabled={loading}
                  className="px-4 py-2 border border-amber-300 bg-amber-50 text-amber-800 text-sm font-medium rounded-lg hover:bg-amber-100 disabled:opacity-40 transition-colors"
                >
                  Apply valid subset ({validPairs} of {preview.totals.pairs})…
                </button>
              )}
              <button
                onClick={() => setConfirmAll(true)}
                disabled={loading || validPairs === 0 || invalidPairs > 0}
                title={invalidPairs > 0 ? 'Fix all issues to apply everything' : undefined}
                className="px-5 py-2 bg-sky-700 text-white text-sm font-medium rounded-lg hover:bg-sky-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {`Update ${validPairs * 2} Orders…`}
              </button>
            </>
          )}
          {step === 'applied' && (
            <button
              onClick={onClose}
              className="px-5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
